import type {
  ClosingPackageCompositionResult,
  ClosingPackageItemState,
  ClosingPackageProjection,
  CompositionMode,
  CompositionStatus,
} from '../types';
import {
  buildCompositionTableRows,
  filterCompositionTableRows,
  type CompositionTableRow,
} from './compositionProjectionAdapter';
import { resolveCompositionRowSource, type CompositionRowSource } from './compositionRowSource';

/**
 * Unified row model for Composition Editor.
 * Merge-only — no permissions, workflow, or action logic.
 */
export type CompositionEditorRow = CompositionTableRow & {
  itemKey: string;
  routeId: number | null;
  compositionItemId: number | null;
  compositionStatus: CompositionStatus;
  isManual: boolean;
  linkedDocumentId: number | null;
  sortOrder: number | null;
  source: CompositionRowSource;
  canEdit: boolean;
};

export type CompositionEditorFilter = 'all' | 'required' | 'problems' | 'excluded';

export type BuildCompositionEditorRowsInput = {
  projection: ClosingPackageProjection;
  composition?: ClosingPackageCompositionResult | null;
  canEdit: boolean;
};

type CompositionIdentity = {
  id: number;
  itemStatus: CompositionStatus;
  isManual: boolean;
  documentId: number | null;
  sortOrder: number;
};

function compositionLookupKey(itemKey: string, routeId: number | null): string {
  return `${itemKey}::${routeId ?? 0}`;
}

function buildCompositionIdentityMap(
  composition: ClosingPackageCompositionResult | null | undefined
): Map<string, CompositionIdentity> {
  const map = new Map<string, CompositionIdentity>();
  if (!composition?.items?.length) {
    return map;
  }

  for (const item of composition.items) {
    map.set(compositionLookupKey(item.itemKey, item.routeId), {
      id: item.id,
      itemStatus: item.itemStatus,
      isManual: item.isManual,
      documentId: item.documentId,
      sortOrder: item.sortOrder,
    });
  }

  return map;
}

function mapTableRowToEditorRow(
  row: CompositionTableRow,
  compositionMap: Map<string, CompositionIdentity>,
  canEdit: boolean,
  compositionMode: CompositionMode
): CompositionEditorRow {
  const meta = row.item.compositionMeta;
  const itemKey = meta?.itemKey ?? row.item.key;
  const routeId = meta?.routeId ?? null;
  const merged = compositionMap.get(compositionLookupKey(itemKey, routeId));
  const compositionItemId = merged?.id ?? meta?.compositionItemId ?? null;
  const isManual = merged?.isManual ?? meta?.isManual ?? false;

  return {
    ...row,
    itemKey,
    routeId,
    compositionItemId,
    compositionStatus: merged?.itemStatus ?? meta?.compositionStatus ?? 'INCLUDED',
    isManual,
    linkedDocumentId: merged?.documentId ?? meta?.linkedDocumentId ?? null,
    sortOrder: merged?.sortOrder ?? meta?.sortOrder ?? null,
    source: resolveCompositionRowSource({
      compositionMode,
      isManual,
      compositionItemId,
      compositionMeta: meta,
    }),
    canEdit,
  };
}

function sortEditorRows(rows: CompositionEditorRow[]): CompositionEditorRow[] {
  const hasSortOrder = rows.some((row) => row.sortOrder != null);
  if (!hasSortOrder) {
    return rows;
  }

  return [...rows].sort((a, b) => {
    const orderA = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) return orderA - orderB;
    return a.rowKey.localeCompare(b.rowKey);
  });
}

/**
 * Projection (+ optional Composition GET) → editor rows.
 * Composition GET supplies identity fields only; display comes from Projection.
 */
export function buildCompositionEditorRows(input: BuildCompositionEditorRowsInput): CompositionEditorRow[] {
  const tableRows = buildCompositionTableRows(input.projection, { includeExcluded: true });
  const compositionMap = buildCompositionIdentityMap(input.composition);

  return sortEditorRows(
    tableRows.map((row) =>
      mapTableRowToEditorRow(row, compositionMap, input.canEdit, input.projection.compositionMode)
    )
  );
}

export function filterCompositionEditorRows<T extends CompositionEditorRow>(
  rows: T[],
  filter: CompositionEditorFilter
): T[] {
  if (filter === 'excluded') {
    return rows.filter((row) => row.compositionStatus === 'EXCLUDED');
  }
  return filterCompositionTableRows(rows, filter);
}

export type { ClosingPackageItemState };

